import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import {
  formatForInvoiceDisplay,
  formatAmount,
} from '../../../../helpers';
import useStyles from './styles';

const InvoiceLineItems = ({ invoice, duplicateSessionIds }) => {
  const { lines, currency } = invoice;
  const { classes } = useStyles();
  const hasDuplicateSessionIds = duplicateSessionIds.length > 0;

  return (
    <Grid container style={{ paddingTop: 16 }}>
      <Grid item xs={12}>
        <Typography variant="subtitle2">
          <strong>Invoice Line Items</strong>
        </Typography>
        <Divider />
      </Grid>
      {hasDuplicateSessionIds && (
        <Grid item xs={12} className={classes.duplicateWarning}>
          <Alert severity="warning">
            Duplicate lesson IDs found: {duplicateSessionIds.join(', ')}. This invoice should be reviewed before payment.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <TableContainer style={{ overflow: 'auto', maxHeight: 300 }}>
          <Table size="small" stickyHeader aria-label="invoice line items">
            <TableHead>
              <TableRow>
                <TableCell><strong>Lesson Id</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell align="right"><strong>Duration (hours)</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => {
                const sessionId = line.metadata.session.id;
                const isDuplicateLine = duplicateSessionIds.includes(sessionId);

                return (
                  <TableRow key={line.id} className={isDuplicateLine ? classes.duplicateLine : undefined}>
                    <TableCell component="th" scope="row" className={classes.id}>
                      {sessionId}
                    </TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell>{formatForInvoiceDisplay(
                      line.metadata.session.date,
                      line.metadata.session.totalHoursTutored,
                    )}
                    </TableCell>
                    <TableCell align="right">{line.metadata.session.totalHoursTutored}</TableCell>
                    <TableCell align="right">{formatAmount(line.amount, line.currency)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer>
          <Table size="small" stickyHeader aria-label="invoice line items total">
            <TableBody>
              <TableRow>
                <TableCell align="right" style={{ border: 0, paddingRight: '30px' }}>
                  <strong>Total</strong>&nbsp;
                  {formatAmount(lines.reduce((total, item) => total + item.amount, 0), currency)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

InvoiceLineItems.defaultProps = {
  duplicateSessionIds: [],
};

InvoiceLineItems.propTypes = {
  invoice: PropTypes.shape({
    lines: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        session: PropTypes.shape({
          id: PropTypes.string.isRequired,
          date: PropTypes.number.isRequired,
          totalHoursTutored: PropTypes.number.isRequired,
        }).isRequired,
      }).isRequired,
    })).isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  duplicateSessionIds: PropTypes.arrayOf(PropTypes.string),
};

export default InvoiceLineItems;
